<template>
  <div>

    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Expense from ${getVendorName()}` : "New expense" }}
              <CBadge
                v-if="obj.id"
                :color="badgeColor('expense', obj.expenseStateDescription || 'Draft')"
                class="ml-2 align-middle"
              >
                {{ badgeLabel('expense', obj.expenseStateDescription || 'Draft') }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{ obj.id ? "General expense" : "Record a general business expense" }}
            </p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" @click="startTour">
              <CIcon name="cil-info" class="mr-1" /> Take a tour
            </CButton>
            <CDropdown
              placement="bottom-end"
              toggler-text="Actions"
              color="light"
              class="tour-cdropdown"
            >
              <CDropdownItem @click="addNew()">
                <CIcon name="cil-plus" class="mr-2" />
                New expense
              </CDropdownItem>
              <CDropdownItem v-if="obj.id" @click="onConvertToInvoice(obj)">
                <CIcon name="cil-copy" class="mr-2" />
                Duplicate
              </CDropdownItem>
              <template v-if="obj.id && computedExpenseStatuses.length">
                <CDropdownDivider />
                <CDropdownItem
                  v-for="status in computedExpenseStatuses"
                  :key="status.id"
                  @click="changeState(status)"
                >
                  {{ statusActionLabel(status) }}
                </CDropdownItem>
              </template>
            </CDropdown>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0 shadow-sm">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h5 class="mb-0">Expense details</h5>
          </CCardHeader>
          <CCardBody class="p-4">
            <!-- Vendor & Expense Info -->
            <CRow>
              <CCol md="6">
                <VendorSelectComponent
                  :selectedVendor="selectedVendor"
                  :vendorItems="vendorItemsWithAddNew"
                  @vendor-selected="handleVendorSelect"
                  @add-new-vendor="addNewVendor"
                />

                <div
                  v-if="selectedVendor && selectedVendor.id !== 'add_new'"
                  class="vendor-details p-3 bg-light rounded mb-3"
                >
                  <div class="d-flex align-items-center mb-2">
                    <div
                      class="c-avatar c-avatar-sm bg-primary text-white mr-2"
                    >
                      {{
                        selectedVendor.name
                          ? selectedVendor.name.charAt(0)
                          : "?"
                      }}
                    </div>
                    <div class="font-weight-bold">
                      {{ selectedVendor.name }}
                    </div>
                    <CButton
                      color="link"
                      size="sm"
                      class="ml-auto"
                      @click="viewVendorDetails"
                      v-if="selectedVendor.id !== 'add_new'"
                    >
                      View vendor
                    </CButton>
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedVendor.address"
                  >
                    <CIcon name="cil-location-pin" />
                    {{ selectedVendor.address }}
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedVendor.contactEmail"
                  >
                    <CIcon name="cil-envelope-closed" />
                    {{ selectedVendor.contactEmail }}
                  </div>
                  <div
                    class="small text-muted mb-1"
                    v-if="selectedVendor.contactPhone"
                  >
                    <CIcon name="cil-phone" />
                    {{ selectedVendor.contactPhone }}
                  </div>
                  <div
                    v-if="selectedVendor.bankName || selectedVendor.bankAccountName || selectedVendor.bankAccountNo"
                    class="mt-2 pt-2 border-top"
                  >
                    <div class="small font-weight-bold text-primary mb-1">
                      <CIcon name="cil-bank" />
                      Bank details
                    </div>
                    <div
                      class="small text-muted mb-1"
                      v-if="selectedVendor.bankName"
                    >
                      <strong>Bank:</strong> {{ selectedVendor.bankName }}
                    </div>
                    <div
                      class="small text-muted mb-1"
                      v-if="selectedVendor.bankAccountName"
                    >
                      <strong>Account name:</strong> {{ selectedVendor.bankAccountName }}
                    </div>
                    <div
                      class="small text-muted"
                      v-if="selectedVendor.bankAccountNo"
                    >
                      <strong>Account no.:</strong> {{ selectedVendor.bankAccountNo }}
                    </div>
                  </div>
                </div>
              </CCol>

              <CCol md="6">
                <CRow>
                  <CCol sm="6">
                    <CFormGroup wrapperClasses="input-group pt-2">
                      <template #label>
                        <CIcon name="cil-calendar" class="mr-1" />
                        Date *
                      </template>
                      <template #input>
                        <CInput
                          ref="dateField"
                          type="date"
                          class="w-100 mb-0"
                          :value="computeIssuedDate"
                          :is-valid="errors.date ? false : null"
                          :invalid-feedback="errors.date"
                          @change="setIssuedDate"
                        />
                      </template>
                    </CFormGroup>
                  </CCol>
                  <CCol sm="6">
                    <CFormGroup wrapperClasses="input-group pt-2">
                      <template #label>
                        <CIcon name="cil-dollar" class="mr-1" />
                        Total amount *
                      </template>
                      <template #input>
                        <CInput
                          ref="amountField"
                          class="w-100 mb-0"
                          v-model="obj.totalAmount"
                          type="number"
                          inputmode="decimal"
                          step="0.01"
                          placeholder="0.00"
                          prepend="RM"
                          :is-valid="errors.totalAmount ? false : null"
                          :invalid-feedback="errors.totalAmount"
                          @input="errors.totalAmount = ''"
                        />
                      </template>
                    </CFormGroup>
                  </CCol>
                </CRow>

              </CCol>
            </CRow>

            <!-- Payment Information Section -->
            <div v-if="obj.expenseState >= 1" class="payment-section mt-4 p-3 bg-light rounded">
              <h6 class="mb-3">
                <CIcon name="cil-credit-card" class="mr-2" />
                Payment
              </h6>

              <CRow>
                <!-- Is Paid Toggle -->
                <CCol md="6">
                  <CRow form class="form-group">
                    <CCol tag="label" sm="4" class="col-form-label">
                      <CIcon name="cil-check-circle" class="mr-1" />
                      Paid
                    </CCol>
                    <CCol sm="8">
                      <CSwitch
                        class="mr-1"
                        color="success"
                        :key="'paid-' + switchKey"
                        :checked="obj.isPaid"
                        label-on="Yes"
                        label-off="No"
                        aria-label="Paid"
                        :disabled="obj.expenseStateDescription !== 'Approve' && obj.expenseStateDescription !== 'Paid'"
                        @update:checked="askPaymentStatusChange"
                      />
                      <div
                        v-if="obj.expenseStateDescription !== 'Approve' && obj.expenseStateDescription !== 'Paid'"
                        class="text-muted small mt-1"
                      >
                        Approve the expense first, then you can mark it as paid.
                      </div>
                    </CCol>
                  </CRow>
                </CCol>
              </CRow>

              <!-- Payment Details (conditional) -->
              <CRow v-if="obj.isPaid || obj.expenseStateDescription === 'Paid'">
                <CCol md="6">
                  <CFormGroup wrapperClasses="input-group pt-2">
                    <template #label>
                      <CIcon name="cil-calendar" class="mr-1" />
                      Payment date *
                    </template>
                    <template #input>
                      <CInput
                        type="date"
                        :value="computePaymentDate"
                        @change="setPaymentDate"
                      />
                    </template>
                  </CFormGroup>
                </CCol>

                <CCol md="6">
                  <CFormGroup wrapperClasses="input-group pt-2">
                    <template #label>
                      <CIcon name="cil-credit-card" class="mr-1" />
                      Payment method *
                    </template>
                    <template #input>
                      <v-select
                        v-model="obj.paymentMethod"
                        :options="paymentMethodOptions"
                        placeholder="Select payment method"
                      />
                    </template>
                  </CFormGroup>
                </CCol>
              </CRow>

              <CRow v-if="obj.isPaid || obj.expenseStateDescription === 'Paid'">
                <CCol md="12">
                  <CFormGroup wrapperClasses="input-group pt-2">
                    <template #label>
                      <CIcon name="cil-file" class="mr-1" />
                      Payment reference
                    </template>
                    <template #input>
                      <CInput
                        v-model="obj.paymentReference"
                        placeholder="Cheque no., transfer ID or receipt no."
                      />
                    </template>
                  </CFormGroup>
                </CCol>
              </CRow>

              <CRow v-if="obj.isPaid || obj.expenseStateDescription === 'Paid'">
                <CCol md="12">
                  <CFormGroup>
                    <template #label>
                      <CIcon name="cil-notes" class="mr-1" />
                      Payment notes
                    </template>
                    <template #input>
                      <CTextarea
                        v-model="obj.paymentNotes"
                        placeholder="Anything else about this payment"
                        rows="2"
                      />
                    </template>
                  </CFormGroup>
                </CCol>
              </CRow>
            </div>

            <!-- Description -->
            <CFormGroup>
              <template #label>
                <CIcon name="cil-description" class="mr-1" />
                Description
              </template>
              <template #input>
                <CTextarea
                  v-model="obj.description"
                  placeholder="What was this expense for?"
                  rows="4"
                />
              </template>
            </CFormGroup>
          </CCardBody>
          <CCardFooter
            class="bg-transparent d-flex justify-content-between p-4"
          >
            <CButton color="light" :disabled="saving" @click="cancel">Cancel</CButton>
            <CButton color="primary" :disabled="saving" @click="submit" class="tour-submit-button">
              <CSpinner v-if="saving" size="sm" class="mr-1" />
              {{ obj.id ? "Save" : "Create expense" }}
            </CButton>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>

    <!-- Document Upload Section -->
    <CRow>
      <CCol md="4" v-for="docType in documentTypes" :key="docType.key">
        <CCard class="mb-4 border-0 shadow-sm h-100">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h6 class="mb-0">
              <CIcon :name="docType.icon" class="mr-2" />
              {{ docType.title }}
            </h6>
          </CCardHeader>
          <CCardBody class="p-4">
            <!-- Existing Document Display -->
            <div v-if="obj[docType.documentField]" class="mb-3">
              <div class="text-center mb-2">
                <div v-if="isPdf(obj[docType.documentField])">
                  <iframe
                    :src="getDocumentUrl(obj[docType.documentField])"
                    width="100%"
                    height="200"
                    class="border rounded"
                  ></iframe>
                </div>
                <div v-else>
                  <CImg
                    :src="getDocumentUrl(obj[docType.documentField])"
                    class="img-thumbnail"
                    style="max-height: 200px; width: 100%;"
                  />
                </div>
              </div>
              <div class="d-flex justify-content-between">
                <CButton
                  color="light"
                  size="sm"
                  @click="openDocument(obj[docType.documentField])"
                >
                  <CIcon name="cil-external-link" class="mr-1" />
                  View
                </CButton>
                <CButton
                  color="danger"
                  variant="outline"
                  size="sm"
                  @click="removeDocument(docType.key)"
                >
                  <CIcon name="cil-trash" class="mr-1" />
                  Remove
                </CButton>
              </div>
            </div>

            <!-- Upload Component -->
            <WidgetsUploadImage
              :billImageUrl="getImageUrl(docType.key)"
              @uploaded="(data) => handleUpload(data, docType.key)"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Modals -->
    <!-- Add Vendor Modal -->
    <VendorFormModal
      :show="addVendorFormPopup"
      :vendor="itemAddNewVendor"
      @close="addVendorFormPopup = false"
      @save="onVendorPopupConfirmation"
    />

    <!-- Chart of Account Selection Modal -->
    <CModal
      title="Select account"
      size="lg"
      :show.sync="chooseAccountPopup"
      @update:show="onAccountPopupConfirmation"
    >
      <CFormGroup>
        <CLabel>Account *</CLabel>
        <v-select
          v-model="selectedChartAccount"
          :label="`itemDisplay`"
          :options="filteredChartAccounts"
          placeholder="Search accounts"
        />
      </CFormGroup>
      <template #footer>
        <CButton color="light" @click="chooseAccountPopup = false">
          Cancel
        </CButton>
        <CButton
          color="primary"
          :disabled="!selectedChartAccount"
          @click="confirmAccountSelection"
        >
          Use this account
        </CButton>
      </template>
    </CModal>

    <!-- Delete Confirmation Modals -->
    <CModal
      v-for="docType in documentTypes"
      :key="`modal-${docType.key}`"
      :title="`Remove the ${docType.title.toLowerCase()}?`"
      :show.sync="deleteModals[docType.key]"
      @update:show="(val) => onDeleteConfirmation(val, docType.key)"
    >
      The uploaded {{ docType.title.toLowerCase() }} will be removed from this expense. This can't be undone.
      <template #footer>
        <CButton color="light" @click="deleteModals[docType.key] = false">
          Keep it
        </CButton>
        <CButton color="danger" @click="confirmDelete(docType.key)">
          Remove document
        </CButton>
      </template>
    </CModal>

    <!-- Status / payment change confirmation (posts to the accounts) -->
    <CModal
      :title="stateConfirmTitle"
      :show.sync="stateConfirm.show"
      :close-on-backdrop="!stateConfirm.busy"
      @update:show="onStateConfirmShow"
    >
      <p class="mb-0">{{ stateConfirmMessage }}</p>
      <template #footer>
        <CButton color="light" :disabled="stateConfirm.busy" @click="cancelStateChange">Cancel</CButton>
        <CButton
          :color="stateConfirm.kind === 'Cancelled' ? 'danger' : 'primary'"
          :disabled="stateConfirm.busy"
          @click="confirmStateChange"
        >
          <CSpinner v-if="stateConfirm.busy" size="sm" class="mr-1" />
          {{ stateConfirmButton }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import Shepherd from "shepherd.js";
import "shepherd.js/dist/css/shepherd.css";

import ExpenseApi from "../../lib/expenseApi";
import ResitAiApi from "@/lib/resitaiApi";
import DocumentApi from "@/lib/documentApi";
import ChartOfAccountApi from "../../lib/chartOfAccountApi";
import WidgetsUploadImage from "../widgets/WidgetsUploadImage.vue";
import { formatMyr } from "@/utils/money";
import { mytToday, asDateString, formatDate, formatDateTime } from "@/utils/datetime";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import VendorApi from "@/lib/vendorApi";

// Import reusable components (you'll need to create these based on your quotation components)
import VendorSelectComponent from "../components/VendorSelectComponent";
import VendorFormModal from "../components/VendorFormModal";

export default {
  name: "OtherExpense",
  components: {
    WidgetsUploadImage,
    vSelect,
    VendorSelectComponent,
    VendorFormModal,
  },
  data() {
    return {
      chooseAccountPopup: false,
      selectedChartAccount: null,
      pendingStatusChange: null,
      filteredChartAccounts: [],
      expenseStatuses: [],
      itemAddNewVendor: {},
      addVendorFormPopup: false,
      deleteModals: {
        invoice: false,
        payment: false,
        receipt: false
      },
      vendorApi: new VendorApi(),
      vendorItems: [],
      selectedVendor: null,
      selectedChartOfAccount: null,
      chartOfAccountItems: [],
      isLoading: false,
      isLoadingSearchChartOfAccount: false,
      documentApi: new DocumentApi(),
      obj: {},
      issuedDate: mytToday(),
      paymentDate: mytToday(),
      submitted: false,
      saving: false,
      errors: { date: "", totalAmount: "" },
      // Pending status / payment change waiting for confirmation
      stateConfirm: { show: false, kind: "", status: null, isPaid: null, busy: false },
      switchKey: 0,
      api: new ExpenseApi(),
      resitAiApi: new ResitAiApi(),
      chartOfAccountApi: new ChartOfAccountApi(),
      loading: false,
      documentTypes: [
        {
          key: 'invoice',
          title: 'Invoice/Bill',
          icon: 'cil-description',
          documentField: 'documentInvoice',
          idField: 'documentInvoiceId'
        },
        {
          key: 'payment',
          title: 'Proof of payment',
          icon: 'cil-credit-card',
          documentField: 'documentPayment',
          idField: 'documentPaymentId'
        },
        {
          key: 'receipt',
          title: 'Receipt',
          icon: 'cil-receipt',
          documentField: 'documentReceipt',
          idField: 'documentReceiptId'
        }
      ],
      paymentMethodOptions: [
        "Online Banking",
        "Cash",
        "Others"
      ]
    };
  },
  mounted() {
    var self = this;
    this.$store.commit("setPageNavItems", ["Expenses", "General Expenses"]);
    this.setIssuedDate(mytToday());
    this.setPaymentDate(mytToday());
    this.fetchStatuses();
    self.refreshChartOfAccount();
    self.refreshVendor();
    self.resetObj();

    // Check for pending payroll expense data
    const pendingData = sessionStorage.getItem('pendingPayrollExpense');
    if (pendingData && this.$route.query.fromPayroll === 'true') {
      try {
        const data = JSON.parse(pendingData);
        // Wait for chart accounts and vendors to load before auto-populating
        setTimeout(() => {
          this.autoPopulateFromPayroll(data);
        }, 1000);
        sessionStorage.removeItem('pendingPayrollExpense');
      } catch (error) {
        console.error('Error loading payroll expense data:', error);
        this.$toast.error("Couldn't load the payroll expense. Open it again from the payroll.");
      }
    }
  },
  computed: {
    computedExpenseStatuses() {
      return this.expenseStatuses.filter(
        (status) => status.name !== this.obj.expenseStateDescription && status.name !== 'Paid'
      );
    },
    vendorItemsWithAddNew() {
      return [
        ...this.vendorItems,
        { id: "add_new", name: "+ Add new vendor" },
      ];
    },
    computeIssuedDate() {
      return asDateString(this.issuedDate);
    },
    computePaymentDate() {
      return asDateString(this.paymentDate);
    },
    stateConfirmTitle() {
      const k = this.stateConfirm.kind;
      if (k === "pay") return "Mark this expense as paid?";
      if (k === "unpay") return "Mark this expense as unpaid?";
      if (k === "Approve") return "Approve this expense?";
      if (k === "Draft") return "Move this expense back to draft?";
      if (k === "Cancelled") return "Cancel this expense?";
      return `Change this expense to ${badgeLabel("expense", k).toLowerCase()}?`;
    },
    stateConfirmMessage() {
      const amount = `RM ${formatMyr(this.obj.totalAmount)}`;
      const who = this.getVendorName();
      const k = this.stateConfirm.kind;
      if (k === "pay") return `The ${amount} expense from ${who} will be recorded as paid today in your accounts.`;
      if (k === "unpay") return `The payment for the ${amount} expense from ${who} will be reversed. The expense goes back to approved.`;
      if (k === "Approve") return `The ${amount} expense from ${who} will be approved and posted to your accounts.`;
      if (k === "Draft") return `The ${amount} expense from ${who} goes back to draft. Its posting is reversed so you can change it.`;
      if (k === "Cancelled") return `The ${amount} expense from ${who} will be cancelled and won't count in your reports.`;
      return `The ${amount} expense from ${who} will be changed to ${badgeLabel("expense", k).toLowerCase()}.`;
    },
    stateConfirmButton() {
      const k = this.stateConfirm.kind;
      if (k === "pay") return "Mark as paid";
      if (k === "unpay") return "Mark as unpaid";
      if (k === "Approve") return "Approve expense";
      if (k === "Draft") return "Move to draft";
      if (k === "Cancelled") return "Cancel expense";
      return "Change status";
    },
  },
  methods: {
    badgeColor,
    badgeLabel,

    // Menu wording for a status transition
    statusActionLabel(status) {
      if (status.name === "Approve") return "Approve";
      if (status.name === "Draft") return "Move to draft";
      if (status.name === "Cancelled") return "Cancel expense";
      return badgeLabel("expense", status.name);
    },

    // The "Paid" switch asks first; the switch only flips once the change is saved.
    askPaymentStatusChange(isPaid) {
      if (!this.obj.id) return;
      this.stateConfirm = { show: true, kind: isPaid ? "pay" : "unpay", status: null, isPaid, busy: false };
    },

    onStateConfirmShow(visible) {
      if (!visible && !this.stateConfirm.busy) this.cancelStateChange();
    },

    cancelStateChange() {
      this.switchKey++;
      this.stateConfirm = { show: false, kind: "", status: null, isPaid: null, busy: false };
    },

    async confirmStateChange() {
      if (this.stateConfirm.busy) return;
      this.stateConfirm.busy = true;
      try {
        if (this.stateConfirm.status) {
          this.pendingStatusChange = this.stateConfirm.status;
          await this.proceedChangeState(null);
        } else {
          this.obj.isPaid = this.stateConfirm.isPaid;
          await this.onPaymentStatusChange(this.stateConfirm.isPaid);
        }
      } finally {
        this.cancelStateChange();
      }
    },

    async onPaymentStatusChange(isPaid) {
      // When user toggles "Is Paid", change the expense state
      if (isPaid) {
        // Set payment date to today's calendar date in Malaysia.
        const currentDate = window.helper.mytToday();
        this.setPaymentDate(currentDate);
        this.obj.paymentDate = currentDate;

        // Change status to Paid (ExpenseState.Paid = 2)
        try {
          const paidStatus = this.expenseStatuses.find(s => s.name === 'Paid');
          if (paidStatus && this.obj.id) {
            this.obj.expenseState = paidStatus.id;
            await this.api.updateState(this.obj, null);
            this.$toast.success("Expense marked as paid");
            await this.resetObj();
          }
        } catch (error) {
          console.error('Error marking as paid:', error);
          this.$toast.error("Couldn't mark the expense as paid. " + helper.getErrorMessage(error && error.data, "Please try again."));
          // Revert the toggle
          this.obj.isPaid = false;
        }
      } else {
        // When toggled off, revert back to Approve status
        try {
          const approveStatus = this.expenseStatuses.find(s => s.name === 'Approve');
          if (approveStatus && this.obj.id) {
            this.obj.expenseState = approveStatus.id;
            await this.api.updateState(this.obj, null);
            this.$toast.success("Expense marked as unpaid");
            await this.resetObj();
          }
        } catch (error) {
          console.error('Error marking as unpaid:', error);
          this.$toast.error("Couldn't mark the expense as unpaid. " + helper.getErrorMessage(error && error.data, "Please try again."));
          // Revert the toggle
          this.obj.isPaid = true;
        }
      }
    },
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },

    autoPopulateFromPayroll(data) {
      const self = this;

      try {
        // Set the date
        if (data.date) {
          self.setIssuedDate(data.date);
          self.setPaymentDate(data.date);
        }

        // Set the amount
        if (data.totalAmount) {
          self.obj.totalAmount = data.totalAmount;
        }

        // Set the description
        if (data.description) {
          self.obj.description = data.description;
        }

        // Set the chart account if available
        if (data.chartAccountId && self.chartOfAccountItems.length > 0) {
          const chartAccount = self.chartOfAccountItems.find(
            acc => acc.id === data.chartAccountId
          );
          if (chartAccount) {
            self.selectedChartOfAccount = chartAccount;
            self.obj.chartAccountId = chartAccount.id;
          }
        }

        // Show success notification
        self.$toast.success(`Expense filled in from payroll ${data.payrollNo} for ${data.employeeName}`);

        // Show info about contributions
        if (data.contributions && data.contributions.length > 0) {
          setTimeout(() => {
            self.$toast.info(`Total includes ${data.contributions.map(c => c.type).join(', ')}`);
          }, 500);
        }

      } catch (error) {
        console.error('Error auto-populating from payroll:', error);
        self.$toast.error("Couldn't fill in the payroll expense. Enter the details by hand.");
      }
    },

    addNew() {
      this.$router.push({ path: "/tenants/otherExpense" });
    },
    
    onConvertToInvoice(item) {
      this.$router.push({
        path: `/tenants/OtherExpense/${item.id}/duplicate`,
      });
    },
    
    viewVendorDetails() {
      if (this.selectedVendor && this.selectedVendor.id !== 'add_new') {
        this.$router.push({
          path: `/tenants/Vendor/${this.selectedVendor.id}`,
        });
      }
    },

    getVendorName() {
      return this.selectedVendor?.name || this.obj.vendor?.name || "Unknown Vendor";
    },


    handleVendorSelect(selected) {
      if (selected?.id === "add_new") {
        this.addNewVendor();
        this.selectedVendor = null;
      } else {
        this.selectedVendor = selected;
      }
    },

    changeState(item) {
      this.selectedChartAccount = null;
      // Status changes post to (or reverse from) the accounts: confirm first.
      this.stateConfirm = { show: true, kind: item.name, status: item, isPaid: null, busy: false };
    },

    async getRecommendedAccount(statusName) {
      try {
        if (this.obj.vendorId) {
          const response = await this.api.getRecommendedChartAccount(
            this.obj.vendorId, 
            statusName
          );
          const recommended = this.filteredChartAccounts.find(
            (a) => a.id === response.result
          );
          if (recommended) {
            this.selectedChartAccount = recommended;
          }
        }
      } catch (error) {
        console.error('Error getting recommended account:', error);
      } finally {
        this.chooseAccountPopup = true;
      }
    },

    onAccountPopupConfirmation(visible) {
      this.chooseAccountPopup = visible;
    },

    confirmAccountSelection() {
      this.proceedChangeState(this.selectedChartAccount);
    },

    async proceedChangeState(account) {
      try {
        this.obj.expenseState = this.pendingStatusChange.id;
        const accountId = account?.id || null;
        
        if (this.obj.id) {
          await this.api.updateState(this.obj, accountId);
          this.$toast.success("Status updated");
          this.resetObj();
        }
      } catch (error) {
        this.$toast.error("Couldn't change the status. " + helper.getErrorMessage(error && error.data, "Please try again."));
      } finally {
        this.chooseAccountPopup = false;
        this.pendingStatusChange = null;
      }
    },

    async fetchStatuses() {
      try {
        const response = await this.api.getStatusTypes();
        this.expenseStatuses = response.result;
      } catch (error) {
        console.error("Failed to load expense statuses:", error);
      }
    },

    addNewVendor() {
      this.itemAddNewVendor = {};
      this.addVendorFormPopup = true;
    },

    onVendorPopupConfirmation(vendor) {
      if (vendor) {
        this.vendorApi
          .create(vendor)
          .then((response) => {
            const addedVendor = response.result;
            this.refreshVendor();
            this.selectedVendor = addedVendor;
            this.$toast.success("Vendor added");
          })
          .catch(({ data }) => {
            this.$toast.error("Couldn't add the vendor. " + helper.getErrorMessage(data, "Please try again."));
          });
      }
      this.addVendorFormPopup = false;
      this.itemAddNewVendor = {};
    },

    async refreshVendor() {
      try {
        const response = await this.vendorApi.getListByCurrentBusiness();
        this.vendorItems = response.result;
      } catch (error) {
        console.error('Error fetching vendors:', error);
      }
    },

    async refreshChartOfAccount() {
      try {
        const response = await this.chartOfAccountApi.getListByCurrentBusiness();
        this.chartOfAccountItems = response.result;
      } catch (error) {
        console.error('Error fetching chart of accounts:', error);
      }
    },

    getImageUrl(docType) {
      const idField = this.documentTypes.find(dt => dt.key === docType)?.idField;
      return this.obj[idField] ? apiUrl + "documents/file/" + this.obj[idField] : '';
    },

    handleUpload(data, docType) {
      const uploadedFiles = data.uploadedFiles;
      if (uploadedFiles.length > 0) {
        const idField = this.documentTypes.find(dt => dt.key === docType)?.idField;
        this.obj[idField] = uploadedFiles[0].id;
        this.saveExpense();
      }
    },

    removeDocument(docType) {
      this.deleteModals[docType] = true;
    },

    onDeleteConfirmation(visible, docType) {
      this.deleteModals[docType] = visible;
    },

    async confirmDelete(docType) {
      try {
        const docTypeData = this.documentTypes.find(dt => dt.key === docType);
        const documentId = this.obj[docTypeData.idField];
        
        if (this.obj.id && documentId) {
          // Use the exact method names from your controller
          const methodMap = {
            invoice: 'removeInvoiceDocument',
            payment: 'removePaymentDocument', 
            receipt: 'removeReceiptDocument'
          };
          
          await this.api[methodMap[docType]](this.obj.id, documentId);
          this.resetObj();
          this.$toast.success("Document removed");
        }
      } catch (error) {
        this.$toast.error("Couldn't remove the document. Please try again.");
      } finally {
        this.deleteModals[docType] = false;
      }
    },

    isPdf(document) {
      return document?.contentType === "application/pdf";
    },

    getDocumentUrl(document) {
      return document?.id ? apiUrl + "documents/file/" + document.id : '';
    },

    openDocument(document) {
      if (document?.id) {
        window.open(this.getDocumentUrl(document), '_blank');
      }
    },

    async resetObj() {
      if (this.$route.params.id) {
        try {
          const response = await this.api.get(this.$route.params.id);
          this.obj = response.result;
          if (this.obj.totalAmount) {
            this.obj.totalAmount = this.obj.totalAmount.toFixed(2);
          }
          this.issuedDate = this.obj.date;
          this.selectedVendor = this.obj.vendor;

          // Set payment date if exists
          if (this.obj.paymentDate) {
            this.paymentDate = this.obj.paymentDate;
          }

          if (this.$route.name === "OtherExpenseDuplicate") {
            this.handleDuplication();
          }
        } catch (error) {
          this.$toast.error("Couldn't load the expense. Check your connection and try again.");
        }
      } else {
        this.obj = this.getEmptyObj();

        // Default to today's date in Malaysia
        this.issuedDate = mytToday();
      }
    },

    handleDuplication() {
      // Reset fields for duplication
      delete this.obj.id;
      delete this.obj.documentInvoice;
      delete this.obj.documentPayment;
      delete this.obj.documentReceipt;
      delete this.obj.documentInvoiceId;
      delete this.obj.documentPaymentId;
      delete this.obj.documentReceiptId;
      delete this.obj.vendor;

      this.issuedDate = window.helper.mytToday();
      this.obj.createdAt = null;
      this.obj.updatedAt = null;
      
      this.$toast.info("This is a copy. Make your changes, then save");
    },

    setIssuedDate(dateString) {
      this.issuedDate = dateString;
      if (dateString) this.errors.date = "";
    },

    setPaymentDate(dateString) {
      this.paymentDate = dateString;
    },

    async saveExpense() {
      try {
        this.obj.date = this.issuedDate;
        if (this.paymentDate) {
          this.obj.paymentDate = this.paymentDate;
        }
        if (this.selectedVendor && this.selectedVendor.id !== 'add_new') {
          this.obj.vendorId = this.selectedVendor.id;
        }

        if (!this.obj.id) {
          const response = await this.api.create(this.obj);
          this.obj = response.result;
          this.$router.push({ path: `/tenants/OtherExpense/${this.obj.id}` });
          this.$toast.success("Expense created");
        } else {
          await this.api.update(this.obj);
          this.$toast.success("Expense saved");
        }
      } catch (error) {
        this.$toast.error("Couldn't save the expense. " + helper.getErrorMessage(error && error.data, "Please try again."));
      }
    },

    async submit() {
      if (this.saving) return;
      this.errors = { date: "", totalAmount: "" };
      if (!this.issuedDate) this.errors.date = "Choose the date of the expense.";
      if (!(parseFloat(this.obj.totalAmount) > 0)) this.errors.totalAmount = "Enter an amount above RM 0.00.";
      if (this.errors.date || this.errors.totalAmount) {
        this.$nextTick(() => {
          const ref = this.errors.date ? this.$refs.dateField : this.$refs.amountField;
          const input = ref && ref.$el && ref.$el.querySelector("input");
          if (input) {
            input.scrollIntoView({ behavior: "smooth", block: "center" });
            input.focus();
          }
        });
        return;
      }
      this.saving = true;
      this.submitted = true;
      try {
        await this.saveExpense();
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.$router.push({ path: "/tenants/otherExpenseList" });
    },

    getEmptyObj() {
      return {
        date: mytToday(),
        totalAmount: 0,
        description: '',
        expenseStateDescription: 'Draft',
        paymentMethod: '',
        paymentReference: '',
        paymentNotes: '',
        isPaid: false
      };
    },

    getDisplayDate(date) {
      return formatDate(date);
    },

    getDisplayDateTime(dateTime) {
      return formatDateTime(dateTime);
    },

    // Tour functionality like in Quotation
    startTour() {
      const tour = new Shepherd.Tour({
        useModalOverlay: true,
        cancelIcon: { enabled: true },
        defaultStepOptions: {
          scrollTo: { behavior: "smooth", block: "center" },
          classes: "shadow-md",
        },
      });

      tour.addStep({
        id: "expense-header",
        text: "This is the expense header where you can see the expense status and actions.",
        attachTo: { element: ".tour-cdropdown", on: "bottom" },
        buttons: [{ text: "Next", action: tour.next }],
      });

      tour.addStep({
        id: "submit-button",
        text: "Click here to submit your completed expense.",
        attachTo: { element: ".tour-submit-button", on: "right" },
        buttons: [
          { text: "Back", action: tour.back },
          { text: "Finish", action: tour.complete },
        ],
      });

      tour.start();
    }
  }
};
</script>

<style scoped>
.vendor-details {
  transition: all 0.3s ease;
}


.img-thumbnail {
  object-fit: cover;
}

.input-group .v-select {
  border-radius: 0.25rem 0 0 0.25rem;
}


.badge-lg {
  font-size: 0.875rem;
  padding: 0.5rem 0.75rem;
}

/* Mobile responsiveness */
@media (max-width: 767.98px) {
  .vendor-details {
    margin-bottom: 1rem;
  }
  
  .btn-group {
    flex-wrap: wrap;
  }
  
  .btn-group .btn {
    margin-bottom: 0.25rem;
  }
}
</style>